import { logger } from "../logger";
import SkybusProviderBase from "../provider/base";
import { WireMsg, WireType } from "../types";
import { debugLog } from "./debugConsoleLog";

// tslint:disable: max-classes-per-file

// TODO(danny): rename to Subscription?
export class Listener<D extends WireMsg> {
  constructor(readonly parent: Subscription<D>) {}
  public stop = (): void => {
    this.parent.removeListener(this);
  };
}

export type LatestListenerCallback<D extends WireMsg> = (msg: D) => void;

export class LatestListener<D extends WireMsg> extends Listener<D> {
  constructor(
    parent: Subscription<D>,
    readonly callback: LatestListenerCallback<D>
  ) {
    super(parent);
  }
}

export type HistoryListenerCallback<D extends WireMsg> = (msgs: D[]) => void;

export class HistoryListener<D extends WireMsg> extends Listener<D> {
  constructor(
    parent: Subscription<D>,
    readonly callback: HistoryListenerCallback<D>
  ) {
    super(parent);
  }
}

// TODO(danny): rename to Channel?
export class Subscription<D extends WireMsg> {
  private latestListeners: Array<LatestListener<D>>;
  private historyListeners: Array<HistoryListener<D>>;

  private subscribed: boolean;

  constructor(
    readonly provider: SkybusProviderBase,
    readonly channel: string,
    readonly type: WireType<D>
  ) {
    this.latestListeners = [];
    this.historyListeners = [];

    this.subscribed = false;
  }

  public listenLatest = (callback: LatestListenerCallback<D>): LatestListener<D> => {
    const listener = new LatestListener(this, callback);
    this.latestListeners.push(listener);
    this.subscribeCheck();
    return listener;
  };

  public listenHistory = (callback: HistoryListenerCallback<D>): HistoryListener<D> => {
    const listener = new HistoryListener(this, callback);
    this.historyListeners.push(listener);
    const channelInfo = this.provider.getChannelInfo(this.channel);
    if (channelInfo && channelInfo.history.length) {
      // we are certain that the type of the history will be of type D
      callback(channelInfo.history as D[]);
    }
    this.subscribeCheck();
    return listener;
  };

  public removeListener = (listener: Listener<D>): void => {
    let listeners: Array<Listener<D>> | undefined;

    if (listener instanceof LatestListener) {
      listeners = this.latestListeners;
    } else if (listener instanceof HistoryListener) {
      listeners = this.historyListeners;
    } else {
      throw new Error("Listener not known type!");
    }

    const index = listeners.findIndex(checkListener => {
      return checkListener === listener;
    });
    if (index >= 0) {
      listeners.splice(index, 1);
    }
    this.subscribeCheck();
  };

  public referenceCount = (): number => {
    return this.latestListeners.length + this.historyListeners.length;
  };

  /** @internal */
  public pushNewLatest = (msg: D): void => {
    for (const latestListener of this.latestListeners) {
      latestListener.callback(msg);
    }
  };

  /** @internal */
  public pushNewHistory = (msgs: D[]): void => {
    for (const historyListener of this.historyListeners) {
      historyListener.callback(msgs);
    }
  };

  /**
   * Turn on this subscription. Once the returned promise resolves we know the
   * vehicle has accepted the subscription and will publish data on the desired channel
   */
  public async activate(debugKey?: string): Promise<Subscription<D>> {
    if (this.subscribed) {
      debugLog({
        debugKey,
        channel: this.channel,
        subscriptionAlreadyActive: true,
      });
      return this;
    } else {
      // set subscribed optimistically so we don't subscribe twice if requests arrive rapidly
      this.subscribed = true;
      return this.provider
        .genSubscription(
          this.channel,
          this.type,
          this.pushNewLatest,
          // @ts-ignore
          this.pushNewHistory
        )
        .then(
          () => {
            debugLog({
              debugKey,
              channel: this.channel,
              subscriptionActivated: true,
            });
            return this;
          },
          err => {
            debugLog({
              debugKey,
              channel: this.channel,
              failedToActivateSubscriptionDueTo: err,
            });
            logger.error(err);
            // if we fail to subscribe then clean up on the provider as it doesn't allow
            // duplicate subscriptions and will error if we try again
            this.provider.unsubscribe(this.channel);
            this.subscribed = false;
            // rethrow to keep in failed state, we were just logging and cleaning up
            throw err;
          }
        );
    }
  }

  private subscribeCheck = (): void => {
    if (this.referenceCount() === 0 && this.subscribed) {
      // if we don't have any listeners, we may safely unsubscribe
      this.provider.unsubscribe(this.channel);
      this.subscribed = false;
    } else if (this.referenceCount() !== 0 && !this.subscribed) {
      // we have listeners, but we are not yet subscribed
      // @ts-ignore
      this.provider.subscribe(this.channel, this.type, this.pushNewLatest, this.pushNewHistory);
      this.subscribed = true;
    }
  };
}
